import { useTicketPriorities } from "@jugl-web/domain-resources/tickets/hooks/useTicketPriorities";
import { useTicketStatuses } from "@jugl-web/domain-resources/tickets/hooks/useTicketStatuses";
import { isTicketOverdue } from "@jugl-web/domain-resources/tickets/utils/isTicketOverdue";
import { TicketListItemModel } from "@jugl-web/rest-api";
import { InteractiveContainer } from "@jugl-web/ui-components";
import { AvatarStack } from "@jugl-web/ui-components/cross-platform";
import { cx } from "@jugl-web/utils";
import { useFormattedDate } from "@jugl-web/utils/hooks/useFormattedDate";
import { FC, useMemo } from "react";
import { TicketPropertyPill } from "../TicketPropertyPill";
import { ReactComponent as CalendarDueIcon } from "./assets/calendar-due.svg";
import { ReactComponent as CalendarIcon } from "./assets/calendar.svg";

interface TicketCardProps {
  ticket: TicketListItemModel;
  className?: string;
  onClick?: () => void;
}

export const TicketCard: FC<TicketCardProps> = ({
  ticket,
  className,
  onClick,
}) => {
  const { getStatusById } = useTicketStatuses();
  const { getPriorityById } = useTicketPriorities();
  const { formatDateTimeLabel } = useFormattedDate();

  const statusOption = getStatusById(ticket.status);
  const priorityOption = getPriorityById(ticket.priority);

  const dueDate = useMemo(
    () => (ticket.due_at ? new Date(ticket.due_at) : null),
    [ticket.due_at]
  );

  const isOverdue = isTicketOverdue({ dueDate, status: ticket.status });

  const DueDateIcon = isOverdue ? CalendarDueIcon : CalendarIcon;

  const assigneeMembers = useMemo(
    () =>
      ticket.assignees.map((user) => ({
        firstName: user.first_name,
        lastName: user.last_name ?? "",
        imageUrl: user.img,
      })),
    [ticket.assignees]
  );

  return (
    <InteractiveContainer
      className={cx(
        "flex flex-col gap-2.5 rounded-xl border border-solid border-[#E0E0E0] bg-white p-4 pb-[18px]",
        className
      )}
      isDisabled={!onClick}
      onClick={onClick}
    >
      <span className="font-secondary text-[13px] leading-[140%] text-[#707577]">
        #{ticket.unique_id}
      </span>
      <span className="jugl__break-word line-clamp-3 font-secondary text-sm leading-[140%] font-medium text-dark">
        {ticket.title}
      </span>

      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <TicketPropertyPill
          icon={statusOption.icon}
          label={statusOption.label}
        />
        {ticket.priority !== "none" && (
          <TicketPropertyPill
            icon={priorityOption.icon}
            label={priorityOption.label}
          />
        )}
        {dueDate && (
          <TicketPropertyPill
            icon={<DueDateIcon className="shrink-0" />}
            label={formatDateTimeLabel(dueDate, "concise")}
            className={cx(isOverdue && "border-tertiary-300")}
          />
        )}
        {assigneeMembers.length > 0 && (
          <AvatarStack
            members={assigneeMembers}
            size="sm"
            maxCount={6}
            className="shrink-0"
          />
        )}
      </div>
    </InteractiveContainer>
  );
};
